import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

type ToastType = 'error' | 'info'

interface Toast {
  id: number
  message: string
  type: ToastType
}

interface ToastContextValue {
  showError: (message: string) => void
  showInfo: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx
}

const AUTO_DISMISS_MS = 7000

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([])
  const idRef = useRef(0)

  const remove = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const push = useCallback(
    (message: string, type: ToastType) => {
      const id = (idRef.current += 1)
      setToasts(prev => [...prev, { id, message, type }])
      window.setTimeout(() => remove(id), AUTO_DISMISS_MS)
    },
    [remove]
  )

  const showError = useCallback(
    (message: string) => push(message || 'Something went wrong.', 'error'),
    [push]
  )
  const showInfo = useCallback((message: string) => push(message, 'info'), [push])

  // Dev helper: run `breadgptTestToast()` in the browser console to preview the
  // error toast without triggering a real failure.
  useEffect(() => {
    ;(window as unknown as Record<string, unknown>).breadgptTestToast = () =>
      showError('This is a test error message.')
    return () => {
      delete (window as unknown as Record<string, unknown>).breadgptTestToast
    }
  }, [showError])

  return (
    <ToastContext.Provider value={{ showError, showInfo }}>
      {children}
      <div className="toast-container" aria-live="assertive" aria-atomic="true">
        {toasts.map(t => (
          <div key={t.id} className={`toast toast--${t.type}`} role="alert">
            <span className="toast__message">{t.message}</span>
            <button
              className="toast__close"
              onClick={() => remove(t.id)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
